import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, PencilIcon, UsersIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Toggle } from '../../components/ui/ChoiceGroup';
import { PreferenceForm } from '../../components/housing/PreferenceForm';
import { MatchCard } from '../../components/housing/MatchCard';
import { candidates, defaultPreferences } from '../../data/roommates';
import { rankCandidates } from '../../utils/compatibility';
import { useAccount } from '../../contexts/AccountContext';
import type { RoommatePreferences } from '../../types/housing';
export function Roommates() {
  const {
    blocked,
    account,
    setVisibility
  } = useAccount();
  const [preferences, setPreferences] = useState<RoommatePreferences | null>(null);
  const [editing, setEditing] = useState(false);
  const [requested, setRequested] = useState<string[]>([]);
  const matches = useMemo(() => {
    if (!preferences) return [];
    return rankCandidates(preferences, candidates).filter(match => !blocked.includes(match.candidate.displayName));
  }, [preferences, blocked]);
  if (!preferences || editing) {
    return <div className="space-y-6" data-id="element-11344" data-mp="3cutv8fenbi2u">
        <Link to="/housing" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-11345" data-mp="di1k1z1cvtoe">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-11346" data-mp="4gbs8s3kasq3" />
          Housing
        </Link>
        <div className="mx-auto max-w-2xl" data-id="element-11347" data-mp="3vnelrq6vhb3h">
          <PageHeader eyebrow="Roommate matching" title={editing ? 'Update your preferences' : 'How do you want to live?'} description="Answer honestly — matching compares your declared preferences with other students' declared preferences. Nothing here is shared publicly." data-id="element-11348" data-mp="3vmcy8m8o6w4v" />
          <div className="mt-6 rounded-card border border-line bg-surface p-5 sm:p-6" data-id="element-11349" data-mp="2ln23anfn3482">
            <PreferenceForm initial={preferences ?? defaultPreferences} submitLabel={editing ? 'Save and see matches' : 'See my matches'} onSubmit={values => {
            setPreferences(values);
            setEditing(false);
          }} data-id="element-11350" data-mp="2qnpv3bbozp3c" />
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-11351" data-mp="27pn7n0ysm07n">
      <Link to="/housing" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-11352" data-mp="27qolteb3v2tl">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-11353" data-mp="1y03p5wezs8db" />
        Housing
      </Link>

      <PageHeader eyebrow="Roommate matching" title={`${matches.length} students with overlapping preferences`} description="Ranked by how closely their declared preferences match yours. Blocked students never appear here." actions={<Button variant="secondary" icon={<PencilIcon className="h-4 w-4" data-id="element-11356" data-mp="10fkeb9w537ld" />} onClick={() => setEditing(true)} data-id="element-11355" data-mp="10e97103hjhpi">
            Edit preferences
          </Button>} data-id="element-11354" data-mp="1k0ua502ivkax" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-11357" data-mp="1abxcol2mq9mg">
        <div className="space-y-3" data-id="element-11358" data-mp="1obycdx75rb0c">
          {matches.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-11359" data-mp="1o9vdsorxw9k0">
              <EmptyState icon={<UsersIcon className="h-4 w-4" data-id="element-11361" data-mp="1jb0xpulcwz3l" />} title="No matches right now" description="Nobody currently matches those preferences at your campus. Widen your budget or accommodation type, or check back — new profiles appear as students start their search." action={<Button variant="secondary" onClick={() => setEditing(true)} data-id="element-11362" data-mp="2314dxrtgxdbe">
                    Adjust preferences
                  </Button>} data-id="element-11360" data-mp="1jcc2nxqjgcwa" />
            </div> : matches.map(match => <MatchCard key={match.candidate.id} match={match} requested={requested.includes(match.candidate.id)} onRequest={id => setRequested(current => [...current, id])} data-id="element-11363" data-mp="5bpgjoyzjrp6" />)}
        </div>

        <div className="space-y-6" data-id="element-11364" data-mp="3dma5v669ghpf">
          <Panel title="Your preferences" bodyClassName="p-0" data-id="element-11365" data-mp="3ijshj4bnxb14">
            <dl className="divide-y divide-line" data-id="element-11366" data-mp="3ikbyoithnve3">
              {[{
              label: 'Budget',
              value: `Up to ₦${preferences.budgetMax.toLocaleString()}`
            }, {
              label: 'Move-in',
              value: preferences.moveIn
            }, {
              label: 'Type',
              value: preferences.kind
            }, {
              label: 'Household',
              value: `${preferences.roommateCount} people`
            }, {
              label: 'Sleep',
              value: preferences.sleep
            }, {
              label: 'Cleanliness',
              value: preferences.tidiness
            }, {
              label: 'Noise',
              value: preferences.noise
            }, {
              label: 'Study',
              value: preferences.study
            }].map(row => <div key={row.label} className="flex justify-between gap-3 px-5 py-2.5" data-id="element-11367" data-mp="2ij6vw9wgpv8y">
                  <dt className="text-[13px] text-ink-muted" data-id="element-11368" data-mp="2dlf3zfvti94r">{row.label}</dt>
                  <dd className="cz-num text-right text-[13px] font-medium text-ink" data-id="element-11369" data-mp="28lsqr6fzosvy">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
          </Panel>

          <Panel title="What others can see" description="You control disclosure before anyone contacts you." data-id="element-11370" data-mp="1zkuj8e6ce38m">
            <Toggle checked={account.visibility.roommate !== 'private'} onChange={value => setVisibility('roommate', value ? 'connections' : 'private')} label="Show my roommate profile" description="Programme, level, bio and preferences. Never your full name, phone or address." data-id="element-11371" data-mp="1unlzwa9gjt5s" />
            <Toggle checked={account.visibility.contact === 'connections'} onChange={value => setVisibility('contact', value ? 'connections' : 'private')} label="Share contact details on mutual accept" description="Phone number is released only when you both accept a request." data-id="element-11372" data-mp="gprhqovsvnfg" />
            <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-11373" data-mp="vns6myy9gz03">
              <EyeIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-11374" data-mp="zmrjd5q8i023" />
              Students see you as “{account.initials === 'AO' ? 'Amarachi O.' : account.initials}”
              until you choose to share more.
            </p>
          </Panel>
        </div>
      </div>
    </div>;
}